import { PluginConfigInterface } from '@cbsinteractive/avia-js';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IAviaPlugin } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPlugin';
import { IAviaPluginsManager } from 'tag/ts/tag/player/adapters/avia/interfaces/IAviaPluginsManager';
import { IReinitializablePluginDiff } from 'tag/ts/tag/player/adapters/avia/interfaces/IReinitializablePluginDiff';
import { AdExpPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/adexp/AdExpPlugin';
import { AdsPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/ads/AdsPlugin';
import { Clid3Plugin } from 'tag/ts/tag/player/adapters/avia/plugins/ads/Clid3Plugin';
import { GamPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/ads/GamPlugin';
import { MetricsPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/diagnostics/MetricsPlugin';
import { MetadataPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/metadata/MetadataPlugin';
import { OmSdkPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/omsdk/OmSdkPlugin';
import { PalPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/pal/PalPlugin';
import { PauseAdsPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/pauseads/PauseAdsPlugin';
import { HlsPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/playback/HlsPlugin';
import { ShakaPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/playback/ShakaPlugin';
import { SessionJsonPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/sessionjson/SessionJsonPlugin';
import { DataHubPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/tracking/DataHubPlugin';
import { TrackingServicePlugin } from 'tag/ts/tag/player/adapters/avia/plugins/tracking/TrackingServicePlugin';
import { PlayerUIPlugin } from 'tag/ts/tag/player/adapters/avia/plugins/ui/PlayerUIPlugin';

/**
 * @copyright © Paramount Streaming
 * @author  Jeff Appis
 * @description manages the instantiation of Avia plugins which are fed into player options
 *
 * **Note:** Nerd Mode is not an avia-js *player* plugin in this roster — it reads metrics via
 * direct polling of the existing `MetricsPlugin` instance instead. (Separately, Nerd Mode is
 * its own standard Tag-level plugin — a different, unrelated plugin system — see `ITagConfig.NerdMode`.)
 */
export class AviaPluginsManager extends AbstractTagClass implements IAviaPluginsManager {

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AviaPluginsManager'
			}
		);
	}

	/**
	 * Debug flags for use during development.
	 * @returns {{forceAllPluginsToLoad: boolean}}
	 */
	get debugging() {
		if ( !this.runtimeVO?.debug?.on ) {
			return {};
		}
		return {
			forceAllPluginsToLoad: false
		};
	}

	get #allPluginsRoster(): IAviaPlugin[] {
		return [
			new TrackingServicePlugin( { tag: this?.tag } ),

			new DataHubPlugin( { tag: this?.tag } ),

			new GamPlugin( { tag: this?.tag } ),

			new Clid3Plugin( { tag: this?.tag } ),

			new PlayerUIPlugin( { tag: this?.tag } ),

			// new DashPlugin( { tag: this?.tag } ),

			new ShakaPlugin( { tag: this?.tag } ),

			new HlsPlugin( { tag: this?.tag } ),

			new MetricsPlugin( { tag: this?.tag } ),

			new SessionJsonPlugin( { tag: this?.tag } ),

			new MetadataPlugin( { tag: this?.tag } ),

			new AdExpPlugin( { tag: this?.tag } ),

			new OmSdkPlugin( { tag: this?.tag } ),

			new PalPlugin( { tag: this?.tag } ),

			new AdsPlugin( { tag: this?.tag } ),

			new PauseAdsPlugin( { tag: this?.tag } )

			// Nerd Mode reads metrics via direct polling of the MetricsPlugin instance above,
			// not as its own entry in this avia-js player plugin roster.
		];
	}

	/**
	 * @description Orchestrates plugin ecosystem initialization with error handling
	 *
	 * **Plugin Ecosystem Orchestration:**
	 * This method serves as the primary entry point for VTG Avia player plugin initialization,
	 * coordinating the plugin ecosystem required for video streaming.
	 * It implements error handling to maintain video service availability.
	 *
	 * **Plugin Initialization Pipeline:**
	 * 1. **Plugin Roster Assembly**: Collects all available plugins (critical + optional)
	 * 2. **Business Logic Evaluation**: Respects each plugin's `shouldBeUsed()` decisions
	 * 3. **Configuration Generation**: Attempts initialization with comprehensive error handling
	 * 4. **Criticality Assessment**: Applies appropriate error responses based on plugin importance
	 * 5. **Service Continuity**: Ensures video playback continues despite non-critical failures
	 *
	 * **Complete Plugin Ecosystem:**
	 * - **Video Decode**: ShakaPlugin (DASH), HlsPlugin (HLS/Live)
	 * - **Monetization**: GamPlugin (Google Ad Manager)
	 * - **Analytics**: TrackingServicePlugin (Data Collection)
	 * - **User Experience**: PlayerUIPlugin (UI Controls)
	 *
	 * **Error Resilience Strategy:**
	 * - **Critical Failures**: Prevent video initialization to avoid broken streaming experience
	 * - **Optional Failures**: Log errors but continue video service for user experience priority
	 * - **Detailed Logging**: Provides operational insights for issue resolution
	 *
	 * **Business Impact:**
	 * - **Service Reliability**: Maintains video streaming availability across platforms
	 * - **Revenue Handling**: Attempts monetization while prioritizing video service
	 * - **System Monitoring**: Plugin status reporting for system health monitoring
	 * - **User Experience**: Video playback continues even with auxiliary feature failures
	 *
	 * @returns {PluginConfigInterface[]} Array of successfully initialized plugin configurations for VTG Avia player
	 * @throws {Error} Only when critical video decode plugins fail to maintain service functionality
	 */
	getAllPlugins = (): PluginConfigInterface[] => {
		const pluginsRoster: IAviaPlugin[] = this.#allPluginsRoster;
		const initializedPlugins: PluginConfigInterface[] = this.#generatePluginConfigs( pluginsRoster );

		this?.logger?.log( 'plugins initialized:', initializedPlugins );

		return initializedPlugins;
	};

	/**
	 * @description Generates Avia plugin configurations with error handling for video availability
	 *
	 * **Error Handling Strategy:**
	 * This method implements plugin loading that allows video playback to continue when
	 * non-essential plugins fail. The error handling categorizes plugins by criticality
	 * and applies appropriate failure responses to maintain video playback functionality.
	 *
	 * **Plugin Classification & Error Response:**
	 *
	 * **Critical Plugins (Video Decode/Streaming):**
	 * - **ShakaPlugin**: DASH video streaming and DRM content protection
	 * - **HlsPlugin**: HLS video streaming and live content support
	 * - **Failure Response**: Throws error and prevents video initialization (fail-fast)
	 * - **Business Rationale**: Video playback impossible without streaming decode capability
	 *
	 * **Optional Plugins (Enhancement/Monetization):**
	 * - **GamPlugin**: Google Ad Manager integration for ad delivery
	 * - **TrackingServicePlugin**: VTG analytics and measurement for data collection (legacy)
	 * - **DataHubPlugin**: DIT DataHub analytics and measurement for data collection
	 * - **PlayerUIPlugin**: UI controls and overlays for player interface
	 * - **Failure Response**: Logged but video playback continues (graceful degradation)
	 * - **Rationale**: Video playback takes priority over non-essential features
	 *
	 * **Error Handling Workflow:**
	 * 1. **Plugin Eligibility Check**: Respects `shouldBeUsed()` business logic per plugin
	 * 2. **Configuration Generation**: Attempts plugin config creation with error capture
	 * 3. **Error Classification**: Categorizes failures by plugin criticality
	 * 4. **Failure Logging**: Comprehensive error tracking for debugging and monitoring
	 * 5. **Critical Assessment**: Throws error only if critical plugins fail
	 * 6. **Graceful Continuation**: Returns successful configs even with optional plugin failures
	 *
	 * **Benefits:**
	 * - **Video Availability**: Streaming continues despite non-critical plugin failures
	 * - **Ad Handling**: Attempts monetization but doesn't block video for ad failures
	 * - **Debugging**: Detailed failure logging for issue resolution
	 * - **Playback Priority**: Video playback takes priority over auxiliary features
	 *
	 * **Debugging Support:**
	 * - **Force Load Override**: Development flag to force all plugins for testing
	 * - **Failure Summary**: Detailed error reporting with plugin categorization
	 * - **Success Metrics**: Counts successful vs. failed plugin initializations
	 *
	 * @param {IAviaPlugin[]} pluginRoster Array of plugin instances to configure
	 * @returns {PluginConfigInterface[]} Successfully configured plugin configs for VTG Avia player
	 * @throws {Error} When critical plugins (Shaka/HLS) fail to initialize, preventing video playback
	 */
	#generatePluginConfigs = ( pluginRoster: IAviaPlugin[] ): PluginConfigInterface[] => {
		const pluginConfigs: PluginConfigInterface[] = [];
		const failures: Array<{ plugin: string, error: any, critical: boolean }> = [];
		const criticalFailures: string[] = [];

		if ( this.debugging?.forceAllPluginsToLoad ) {
			this?.logger?.log( 'Forcing all plugins to load' );
		}

		pluginRoster.forEach( ( plugin: IAviaPlugin ) => {
			if ( !plugin?.shouldBeUsed() && !this.debugging?.forceAllPluginsToLoad ) {
				this?.logger?.log( 'Plugin will not be used:', plugin?.name, plugin );
				return;
			}

			this?.logger?.log( 'Plugin will be imported:', plugin?.name, plugin );

			try {
				const generatedConfig = plugin.generateConfig();

				if ( !this.utils.Type.isNullOrUndefined( generatedConfig ) ) {
					pluginConfigs.push( generatedConfig );
				}
			} catch ( e ) {
				const isCritical = this.#isCriticalPlugin( plugin.name );

				failures.push( {
					plugin: plugin.name,
					error: e,
					critical: isCritical
				} );

				if ( isCritical ) {
					criticalFailures.push( plugin.name );
					this?.logger?.error( 'CRITICAL plugin initialization failed:', plugin.name, e );
				} else {
					this?.logger?.warn( 'Optional plugin initialization failed:', plugin.name, e );
				}
			}
		} );

		// Log summary
		if ( failures.length > 0 ) {
			this?.logger?.warn( 'Plugin initialization summary:', {
				successful: pluginConfigs.length,
				failed: failures.length,
				criticalFailures: criticalFailures.length,
				failures: failures
			} );
		}

		// Throw error if critical plugins failed
		if ( criticalFailures.length > 0 ) {
			throw new Error( `Critical plugins failed to initialize: ${criticalFailures.join( ', ' )}` );
		}

		return pluginConfigs;
	};

	/**
	 * @description Determines plugin criticality for video playback functionality and error handling strategy
	 *
	 * **Critical Plugin Classification:**
	 * This method implements Paramount's plugin criticality assessment that determines error handling
	 * strategy for each plugin type. Critical plugins are essential for core video decode and streaming
	 * functionality - without them, video playback is impossible regardless of content type.
	 *
	 * **Critical Plugins (Required for Video Playback):**
	 * - **ShakaPlugin**: Required for DASH video streaming, DRM content protection, and adaptive streaming
	 *   - Handles protected content with Widevine/PlayReady DRM protection
	 *   - Required for DRM-protected movies and TV content
	 *   - Failure means no DASH content can play
	 *
	 * - **HlsPlugin**: Required for HLS video streaming, live content support, and iOS compatibility
	 *   - Required for live streaming events, sports coverage, and news content
	 *   - Critical for iOS Safari compatibility and mobile web playback
	 *   - Failure means no HLS/live content can play
	 *
	 * **Non-Critical Plugins (Enhancement/Monetization):**
	 * - **GamPlugin**: Google Ad Manager integration for ad delivery
	 * - **TrackingServicePlugin**: VTG analytics and measurement for data collection (legacy)
	 * - **DataHubPlugin**: DIT DataHub analytics and measurement for data collection
	 * - **PlayerUIPlugin**: UI controls and overlays for player interface
	 * - **DashPlugin**: Alternative DASH implementation (currently disabled)
	 *
	 * **Classification Logic:**
	 * The criticality classification prioritizes video functionality over other features:
	 * - **Video Playback > Revenue**: Working video without ads preferred over no video
	 * - **Core Streaming > Enhancements**: Decode capability prioritized over UI features
	 * - **Basic Function > Feature Completeness**: Core video functionality maintained
	 *
	 * **Error Handling Impact:**
	 * - **Critical Plugin Failure**: Throws error, prevents video initialization (fail-fast)
	 * - **Optional Plugin Failure**: Logs warning, allows video playback to continue (graceful degradation)
	 *
	 * @param {string} pluginName Name of the plugin to classify for error handling strategy
	 * @returns {boolean} True if plugin is critical for video decode/streaming (requires fail-fast on error)
	 */
	#isCriticalPlugin = ( pluginName: string ): boolean => {
		const criticalPlugins = [ 'ShakaPlugin', 'HlsPlugin' ];
		return criticalPlugins.includes( pluginName );
	};

	/**
	 * @description Diffs every plugin in the roster that declares itself `isReinitializable()`
	 * against its current `shouldBeUsed()` eligibility, reading current tag params — for
	 * reinitializing plugins whose config or eligibility may go stale when the underlying
	 * avia-js player instance persists across multiple resource loads (e.g.
	 * AviaLifecycleManager's stream-swap reuse path, PLBCK-8461, PLBCK-8475).
	 *
	 * Unlike `#generatePluginConfigs()` (which silently drops any plugin failing
	 * `shouldBeUsed()`), this calls `generateConfig()` unconditionally for the opted-in
	 * roster — safe, since a config-builder factory (e.g. Blackbird's `ads()`/`pal()`) just
	 * returns a plain `{id, factory, options}` object with no side effects until that config
	 * is actually passed to `player.registerPlugins()`. That lets a plugin that's currently
	 * ineligible still report its `id`, so the caller can explicitly remove it instead of it
	 * silently vanishing from the roster with no signal to tear it down.
	 *
	 * Which plugins opt in is a per-plugin decision (see `IAviaPlugin.isReinitializable`) —
	 * this method has no opinion on when or why a reinit happens.
	 * @returns {IReinitializablePluginDiff}
	 */
	getReinitializablePluginDiff = (): IReinitializablePluginDiff => {
		const reinitRoster = this.#allPluginsRoster.filter( ( plugin: IAviaPlugin ) => plugin.isReinitializable() );
		const toRegister: PluginConfigInterface[] = [];
		const idsToRemove: string[] = [];

		reinitRoster.forEach( ( plugin: IAviaPlugin ) => {
			let config: PluginConfigInterface;
			try {
				config = plugin.generateConfig();
			} catch ( e ) {
				this?.logger?.warn( 'getReinitializablePluginDiff:', 'generateConfig failed, skipping:', plugin?.name, e );
				return;
			}

			if ( !config?.id ) {
				this?.logger?.warn( 'getReinitializablePluginDiff:', 'no config/id resolved, skipping:', plugin?.name );
				return;
			}

			if ( plugin.shouldBeUsed() ) {
				toRegister.push( config );
			} else {
				idsToRemove.push( config.id );
			}
		} );

		return { toRegister, idsToRemove };
	};
}